import React, { Suspense, lazy, useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { HERO_ROTATING, HERO_CHIPS } from "@/data/site";
import { track } from "@/lib/api";
import { MagneticButton } from "@/components/pp/Shared";
import { scrollTo } from "@/components/pp/Navbar";

const Hero3D = lazy(() => import("@/components/pp/Hero3D"));

const HEADLINE = [["WE TURN"], ["BOLD"], ["IDEAS"]];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const reduce = useReducedMotion();
  const [webgl, setWebgl] = useState(true);

  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      setWebgl(!!(c.getContext("webgl2") || c.getContext("webgl")));
    } catch { setWebgl(false); }
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_ROTATING.length), 2200);
    return () => clearInterval(t);
  }, []);

  const show3D = webgl && !reduce;

  return (
    <section id="home" className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 pb-16" data-testid="hero">
      {/* Background 3D / fallback */}
      <div className="absolute inset-0 z-0">
        {show3D ? (
          <Suspense fallback={<div className="absolute inset-0" />}>
            <Hero3D />
          </Suspense>
        ) : (
          <div className="absolute inset-0 opacity-70"
            style={{ background: "radial-gradient(circle at 70% 40%, rgba(245,158,11,0.18), transparent 45%), radial-gradient(circle at 40% 70%, rgba(139,92,246,0.18), transparent 50%)" }} />
        )}
      </div>
      {/* vignette to keep text readable */}
      <div className="absolute inset-0 z-[1] pointer-events-none"
        style={{ background: "linear-gradient(90deg, rgba(3,3,5,0.95) 0%, rgba(3,3,5,0.7) 40%, rgba(3,3,5,0.1) 100%)" }} />

      <div className="pp-container relative z-[2] w-full">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="inline-flex items-center gap-2 pp-glass rounded-full px-4 py-1.5 mb-8">
          <Sparkles size={14} className="text-[#f59e0b]" />
          <span className="text-xs tracking-widest uppercase text-slate-200">Creative + Branding + Growth</span>
        </motion.div>

        <h1 className="font-display uppercase tracking-tighter leading-[0.82] text-[3.2rem] sm:text-7xl lg:text-[8rem] text-white max-w-5xl">
          {HEADLINE.map((line, li) => (
            <span key={li} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.4 + li * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                {line[0]}
              </motion.span>
            </span>
          ))}
          <span className="block overflow-hidden">
            <motion.span className="block pp-text-gradient"
              initial={{ y: "110%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.76, ease: [0.22, 1, 0.36, 1] }}>
              THEY CAN'T IGNORE.
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 flex items-center gap-3 text-lg md:text-2xl font-display uppercase tracking-wide">
          <span className="text-slate-400">Powered by</span>
          <span className="relative inline-flex h-[1.4em] min-w-[7ch] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={idx}
                initial={{ y: "100%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "-100%", opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="text-[#f59e0b] absolute"
              >
                {HERO_ROTATING[idx]}
              </motion.span>
            </AnimatePresence>
          </span>
        </div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="mt-6 max-w-xl text-slate-300 text-base md:text-lg leading-relaxed">
          PixelPapad is a creative marketing and digital growth agency delivering branding, social media,
          advertising, content production, celebrity collaborations and immersive digital experiences.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <MagneticButton
            onClick={() => { track("cta_click", { cta: "build_my_brand", location: "hero" }); scrollTo("planner"); }}
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white transition-colors"
          >
            Build My Brand <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </MagneticButton>
          <button
            onClick={() => { track("cta_click", { cta: "explore_work", location: "hero" }); scrollTo("work"); }}
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white/5 transition-colors"
          >
            <Play size={16} /> Explore Our Work
          </button>
          <button
            onClick={() => scrollTo("pricing")}
            data-testid="hero-cta-tertiary"
            className="text-slate-300 hover:text-[#f59e0b] underline underline-offset-4 text-sm transition-colors"
          >
            View Plans
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="mt-12 flex flex-wrap gap-2.5">
          {HERO_CHIPS.map((c) => (
            <span key={c} className="pp-glass rounded-full px-4 py-1.5 text-xs text-slate-200 tracking-wide">{c}</span>
          ))}
        </motion.div>
      </div>

      {/* scroll prompt */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[2] hidden md:flex flex-col items-center gap-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-slate-500">Scroll</span>
        <motion.div animate={{ scaleY: [0.3, 1, 0.3] }} transition={{ duration: 1.8, repeat: Infinity }}
          className="h-10 w-[2px] origin-top bg-[#f59e0b]" />
      </motion.div>
    </section>
  );
}
